/**
 * The dialog a reader draws in, as elements.
 *
 * Apart from `handwriting-panel.ts` so that the panel is the behaviour and this
 * is the markup, which is the split `suggestion-list.ts` and
 * `suggestion-option.ts` already make.
 *
 * **The theme draws none of this.** The pad cannot work without a script, a
 * canvas being a canvas, so there is nothing here for a reader with no script
 * to be given. That is the opposite of the search box itself, which the theme
 * draws and this enhances, and it is why the pen button beside the box is
 * hidden until `handwriting-start.ts` has run.
 *
 * **A `<dialog>`, opened modally.** Four things come with it that would
 * otherwise be written here. It is in the top layer, so it escapes the
 * `overflow: clip` that `modules/cards/wa-card` gives the rail's panel, which
 * the suggestion list needed a rule of its own to get out of. Escape closes it.
 * Focus is held inside it. And the page behind it is inert, which on a phone is
 * what puts the reader's attention on the square rather than on the page
 * scrolling under their hand.
 *
 * `<wa-dialog>` is out for the reason every other control here is native. These
 * pages load Web Awesome's stylesheet and none of its JavaScript.
 */

import { action, element } from "./handwriting-elements.js";

/** Every element of the dialog the panel drives. */
export interface HandwritingElements {
  /** The dialog itself. */
  readonly dialog: HTMLDialogElement;

  /** The square the reader writes in. */
  readonly canvas: HTMLCanvasElement;

  /** Where the candidate characters go. */
  readonly candidates: HTMLElement;

  /** What is said above the candidates while there are none. */
  readonly message: HTMLElement;

  /** Takes back the last stroke. */
  readonly undo: HTMLButtonElement;

  /** Takes back every stroke. */
  readonly clear: HTMLButtonElement;

  /** What the reader has built up, shown back to them. */
  readonly query: HTMLElement;

  /** Runs the search and closes the dialog. */
  readonly search: HTMLButtonElement;
}

/**
 * Builds the dialog, without putting it on the page.
 *
 * The title is the dialog's accessible name, through `aria-labelledby`. One
 * dialog is built per document, so the id it carries is fixed.
 */
export function handwritingDialog(page: Document): HandwritingElements {
  const dialog = element(page, "dialog", "cb-handwriting");
  const titleId = "cb-handwriting-title";
  const title = element(page, "h2", "cb-handwriting-title", "Draw a character");

  title.id = titleId;
  dialog.setAttribute("aria-labelledby", titleId);

  const close = action(page, "cb-handwriting-close", "Close");
  const canvas = element(page, "canvas", "cb-handwriting-pad");

  canvas.setAttribute("role", "img");
  canvas.setAttribute(
    "aria-label",
    "Writing square. Draw a character with a finger, a stylus or the mouse.",
  );

  const candidates = element(page, "div", "cb-handwriting-candidates");

  candidates.setAttribute("role", "group");
  candidates.setAttribute("aria-label", "Characters you may have drawn");

  const message = element(
    page,
    "p",
    "cb-handwriting-message",
    "Draw a character to see what it might be.",
  );

  message.setAttribute("aria-live", "polite");

  const undo = action(page, "cb-handwriting-undo", "Undo stroke");
  const clear = action(page, "cb-handwriting-clear", "Clear");
  const query = element(page, "p", "cb-handwriting-query");
  const search = action(page, "cb-handwriting-search", "Search");
  const head = element(page, "div", "cb-handwriting-head");
  const body = element(page, "div", "cb-handwriting-body");
  const picks = element(page, "div", "cb-handwriting-picks");
  const actions = element(page, "div", "cb-handwriting-actions");
  const foot = element(page, "div", "cb-handwriting-foot");

  head.append(title, close);
  picks.append(message, candidates);
  body.append(canvas, picks);
  actions.append(undo, clear);
  foot.append(query, search);
  dialog.append(head, body, actions, foot);

  // The dialog is closed by Escape, by the button and by a press on the
  // backdrop. The third is this one: a press that lands on the dialog element
  // itself rather than on anything inside it landed outside the panel.
  close.addEventListener("click", () => {
    dialog.close();
  });
  dialog.addEventListener("click", (event) => {
    if (event.target === dialog) {
      dialog.close();
    }
  });

  return {
    dialog,
    canvas,
    candidates,
    message,
    undo,
    clear,
    query,
    search,
  };
}
